import React, { ReactNode } from 'react';

import {
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselOptions,
    CarouselPlugin,
    CarouselPrevious,
} from '../Carousel/carousel';
import { cn } from '../utils';

type ImageCarouselProps = {
    items: ReactNode[];
    setCarouselApi?: (api: CarouselApi) => void;
    className?: string;
    carouselClassName?: string;
    navigationDotsClassName?: string;
    showButtons?: boolean;
    watchDrag?: boolean;
    plugins?: CarouselPlugin;
    opts?: CarouselOptions;
};

export default ({
    items,
    setCarouselApi: setCarouselApiProp,
    className = '',
    carouselClassName = '',
    navigationDotsClassName = '',
    showButtons = true,
    watchDrag = true,
    plugins = [],
    opts,
}: ImageCarouselProps) => {
    const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
    const [current, setCurrent] = React.useState(0);

    React.useEffect(() => {
        if (!carouselApi) {
            return;
        }

        setCurrent(carouselApi.selectedScrollSnap() + 1);

        carouselApi.on('select', () => {
            setCurrent(carouselApi.selectedScrollSnap() + 1);
        });
    }, [carouselApi]);

    React.useEffect(() => {
        if (!carouselApi || !setCarouselApiProp) {
            return;
        }

        setCarouselApiProp(carouselApi);
    }, [carouselApi, setCarouselApiProp]);

    return (
        <div
            className={cn(
                'flex flex-col gap-4 h-full bg-cerulean-600 items-center max-sm:w-full max-sm:mt-[64px]',
                className,
            )}
        >
            <Carousel
                setCarouselApi={setCarouselApi}
                className={cn('flex flex-col gap-8 h-full', carouselClassName)}
                watchDrag={watchDrag}
                plugins={[...plugins]}
                opts={opts}
            >
                <div className="h-full [&_>div]:h-full [&_>div]:flex">
                    <CarouselContent
                        className="flex-grow"
                        style={{ width: `${Math.round(100 / items.length)}%` }}
                    >
                        {items.map((item, index) => (
                            <CarouselItem key={index} className="flex items-center justify-center">
                                {item}
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </div>
                {showButtons && (
                    <>
                        <CarouselPrevious className="max-sm:hidden" />
                        <CarouselNext className="max-sm:hidden" />
                    </>
                )}
                <div
                    className={cn(
                        'flex gap-1 w-full justify-center absolute bottom-12',
                        navigationDotsClassName,
                    )}
                >
                    {Array.from({ length: items.length }).map((_, index) => (
                        // eslint-disable-next-line jsx-a11y/control-has-associated-label
                        <button
                            key={index}
                            type="button"
                            className={cn(
                                'w-2 h-2 rounded-full bg-cerulean-300 cursor-pointer border-none p-0 hover:bg-cerulean-400',
                                index + 1 === current && 'bg-white',
                            )}
                            onClick={() => carouselApi?.scrollTo(index)}
                        />
                    ))}
                </div>
            </Carousel>
        </div>
    );
};
